import { useCallback, useEffect, useMemo, useState } from 'react'
import type { Api } from '../api'
import {
  ApiError,
  type AdminReport,
  type ApiFailure,
  type ColumnGovernance,
  type ColumnGovernanceRow,
  type PermissionCatalogRow,
} from '../api/types'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Loading,
  Notice,
  SearchInput,
  SearchSelect,
} from '../components/ui'
import { DataGrid, type Column } from '../components/DataGrid'
import { FailureState } from '../components/FailureState'
import { SlideOver } from '../components/SlideOver'
import { CheckTile, FilterSlot, InfoStrip, Verdict } from './accessWords'

/**
 * WHICH PERMISSION GATES WHICH FIELD — the fourth gate, and the last one that
 * belongs to this platform.
 *
 * D365 says which MODULES a person holds (AR_Mod, SLS_Mod, Fin_Mod). This
 * platform decides which of ITS columns each module unlocks; the ERP has no
 * opinion about a column called "121 To 180 Days".
 *
 * ===========================================================================
 * THE ONE NUMBER THAT MATTERS ON THIS SCREEN IS PEOPLE, NOT CODES
 *
 * "AR_Mod → Fin_Mod" tells an administrator nothing. "369 people → 43" tells
 * them exactly what they are about to do, and it is what the database records
 * in the audit ledger as COLUMN_PERM_WIDEN or COLUMN_PERM_NARROW. So every
 * permission is offered with its live holder count, the panel states the effect
 * before the press, and the result says it again afterwards in the same terms.
 * Permissions are named as people know them ("Accounts Receivable"), with the
 * code beside, small, for matching a support request.
 *
 * ===========================================================================
 * TWO STATES WORTH SEEING AND EASY TO MISS
 *
 *   UNMAPPED   a column nothing gates is hidden from EVERYONE. It looks like a
 *              column that simply never appears, which reads as a bug in the
 *              report rather than a gap in the configuration. It has its own
 *              card at the top, red while any exist, and a click lists them.
 *
 *   0 HOLDERS  gating a column on a permission nobody holds has the same effect
 *              as unmapping it. Payroll_Mod is held by nobody today, so it is
 *              offered with the count attached and a warning if chosen.
 *
 * Laid out again 27 Oct 2026 with the other Access pages: the three states as
 * cards that filter, the report and the search named above their controls, a
 * line saying what is shown, and the rules at the foot instead of over the top.
 *
 * ===========================================================================
 * WHAT THIS FILE IS NOT
 *
 * It is not a control. The database refuses a non-admin (51180), refuses to
 * restrict a required column (51122), refuses an unknown code (51123) and
 * refuses a change with no reason (51181). The screen states those rules early
 * so they are learned rather than discovered.
 */
export function ColumnGovernanceTab({
  api,
  canEdit,
  isV2 = false,
  reports,
  reportSlug,
  onPickReport,
}: {
  api: Api
  /**
   * System Admin only. A Permission Manager READS this — "why can this person
   * not see Credit Limit?" is their daily question — but re-gating a column
   * changes what every holder of a permission sees on every page at once, which
   * is a data-model decision rather than an access-list one.
   */
  canEdit: boolean
  /**
   * The database runs the new model (060), where a system role sees every
   * column whatever its rule, so an unmapped one is hidden from everyone else.
   */
  isV2?: boolean
  /** The registry, fetched once by the page. Null while it is still loading. */
  reports: AdminReport[] | null
  /** WHICH report's columns are being governed. No default. */
  reportSlug: string
  onPickReport: (slug: string) => void
}) {
  const [data, setData] = useState<ColumnGovernance | null>(null)
  const [search, setSearch] = useState('')
  const [only, setOnly] = useState<GateState | null>(null)
  const [editing, setEditing] = useState<ColumnGovernanceRow | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  // A failed READ of the mapping, kept apart from `error` (which is for a
  // refused re-gate). It used to be folded into an empty mapping, so the tab
  // printed zero counts and "No columns are registered for this report" under
  // the red notice: a confident, false statement about the configuration.
  const [loadFailed, setLoadFailed] = useState<ApiFailure | null>(null)

  const report = reports?.find((r) => r.slug === reportSlug)
  const roleScoped = report?.scopingModel === 'role-scoped'

  const load = useCallback(() => {
    if (!reportSlug) return
    setData(null)
    setDone(null)
    setError(null)
    setLoadFailed(null)
    api
      .columnGovernance(reportSlug)
      .then(setData)
      .catch((e) => {
        setLoadFailed(e instanceof ApiError ? e.failure : { kind: 'error', title: 'Could not read the column mapping.' })
        // Still set, only so the Loading branch ends; nothing below reads these
        // empty lists as a fact while loadFailed is set.
        setData({ columns: [], catalogue: [] })
      })
  }, [api, reportSlug])

  // Re-reads on report change, and drops any notice from the last one — a
  // "column X changed" or a refusal left over from a different report would be
  // read as applying to this one.
  useEffect(load, [load])

  const columns = useMemo(() => data?.columns ?? [], [data])
  const catalogue = useMemo(() => data?.catalogue ?? [], [data])
  const known = !!data && !loadFailed

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    return columns.filter((c) =>
      (!only || stateOf(c) === only)
      && (!q || [c.columnName, c.displayName, ...codesOf(c).flatMap((p) => [p, nameOf(catalogue, p)])].join(' ').toLowerCase().includes(q)),
    )
  }, [columns, catalogue, search, only])

  const count = (s: GateState) => columns.filter((c) => stateOf(c) === s).length
  const hidden = count('hidden')
  const STATES: { state: GateState; name: string; what: string }[] = [
    { state: 'always', name: 'Always shown', what: 'Grouping columns every reader gets. The platform refuses to hide them.' },
    { state: 'gated', name: 'Behind a permission', what: 'Shown to users who hold any one of the permissions listed against it.' },
    {
      state: 'hidden',
      // Under the new model the system roles still see it (060), so it is not
      // hidden from everyone, and the card, the link and the button all say so.
      name: isV2 ? 'System roles only' : 'Hidden from everyone',
      what: isV2
        ? 'Nothing unlocks it, so only the system roles see it. Give it a permission, or retire it.'
        : 'Nothing unlocks it, so nobody sees it, System Admins included. Give it a permission, or retire it.',
    },
  ]
  const stateName = (s: GateState) => STATES.find((x) => x.state === s)?.name ?? s

  const grid: Column<ColumnGovernanceRow>[] = [
    {
      key: 'displayName',
      header: 'Column',
      align: 'left',
      stickyLeft: true,
      sortValue: (c) => c.displayName,
      render: (c) => (
        <span className="flex min-w-0 flex-col items-start text-left">
          <span className="font-medium text-(--color-ink)">{c.displayName}</span>
          {/* The PHYSICAL name under the label. An administrator matching this
              screen against the view, or against a support request quoting a
              column name, needs the string the database actually uses. */}
          <span className="max-w-full truncate font-mono text-(length:--text-micro) text-(--color-ink-3)" title={c.columnName}>
            {c.columnName}
          </span>
        </span>
      ),
    },
    {
      key: 'gate',
      header: 'Unlocked by',
      align: 'left',
      /* SORTS BY WHAT THE CELL SAYS (swept 20 Sep 2026): the three states sort
         as their phrases, so a reader who sorts this column gets them grouped,
         and the hidden ones land at one end rather than among the names. */
      sortValue: (c) =>
        c.isRequired ? 'Always shown' : c.permissions ? codesOf(c).map((p) => nameOf(catalogue, p)).join(', ') : 'Nothing',
      render: (c) =>
        c.isRequired ? (
          <span className="text-(--color-ink-3)" title="A grouping column. Always visible; the platform refuses to restrict it.">
            Always shown
          </span>
        ) : c.permissions ? (
          <span className="inline-flex flex-wrap justify-center gap-1">
            {codesOf(c).map((p) => (
              <PermissionChip key={p} name={nameOf(catalogue, p)} code={p} />
            ))}
          </span>
        ) : (
          <span className="text-(--color-ink-4)">Nothing</span>
        ),
    },
    {
      key: 'people',
      header: 'Who can see it',
      align: 'left',
      /* SORTS BY THE HEADCOUNT THE CELL COMPUTES, not by the text it prints —
         "nobody holds it" and "9 people" would otherwise order alphabetically,
         putting 1,000 before 9. Required columns are everyone, so they sort
         above any real count; a column nobody can see is 0 and sorts to the
         bottom ascending, which is exactly the end an administrator is
         hunting for. Mirrors the render below deliberately: two readings of
         one figure is how a sorted grid ends up disagreeing with itself. */
      sortValue: (c) => (c.isRequired ? Number.MAX_SAFE_INTEGER : holders(catalogue, codesOf(c))),
      // THE COLUMN THAT MAKES THE SCREEN USEFUL. A code is a fact about
      // configuration; a headcount is a fact about people, and it is the one an
      // administrator is actually reasoning about.
      render: (c) => {
        if (c.isRequired) return <span className="text-(--color-ink-2)" title="Everyone who can open the report.">Everyone</span>
        if (!c.permissions) return <Badge tone="bad">{isV2 ? 'System roles only' : 'Nobody'}</Badge>
        const codes = codesOf(c)
        const best = holders(catalogue, codes)
        if (best === 0) return <Badge tone="bad">Nobody holds it</Badge>
        // The largest single permission's holders: one person may hold several, so
        // with two or more listed the true number is at least this.
        return (
          <span className="font-medium text-(--color-ink) tabular-nums">
            {codes.length > 1 ? 'at least ' : ''}{best.toLocaleString('en-GB')} {best === 1 ? 'user' : 'users'}
          </span>
        )
      },
    },
    ...(canEdit
      ? [
          {
            key: 'edit',
            header: ' ',
            align: 'right' as const,
            stickyRight: true,
            render: (c: ColumnGovernanceRow) => (
              // DISABLED WITH A REASON, NOT HIDDEN: the rule is in the tooltip.
              <button
                type="button"
                disabled={c.isRequired}
                title={
                  c.isRequired
                    ? 'Required by the report. Hiding a grouping column breaks every page, so the platform refuses.'
                    : `Change who can see ${c.displayName}`
                }
                onClick={(e) => { e.stopPropagation(); setEditing(c) }}
                className="rounded-md border border-(--color-line-strong) bg-(--color-surface) px-2.5 py-1 text-(length:--text-small) font-medium text-(--color-navy-700) transition-colors hover:border-(--color-navy-300) hover:bg-(--color-navy-50) disabled:cursor-not-allowed disabled:border-(--color-line) disabled:text-(--color-ink-4) disabled:hover:bg-(--color-surface)"
              >
                Change…
              </button>
            ),
          },
        ]
      : []),
  ]

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {(error || done) && (
        <div className="shrink-0 space-y-2">
          {error && <Notice tone="bad" onDismiss={() => setError(null)}>{error}</Notice>}
          {done && <Notice tone="good" onDismiss={() => setDone(null)}>{done}</Notice>}
        </div>
      )}

      <Card className="flex min-h-0 flex-1 flex-col">
        <div className="flex shrink-0 flex-col gap-3 border-b border-(--color-line) px-5 py-(--band-y)">
          {/* One card a state: how many columns are in it, what it means, and a
              click shows only them. '—' until the mapping has been read: a 0
              here would be a claim. Hidden is red while any exist: an unmapped
              column is invisible to every single person, and that is a gap that
              otherwise reads as a missing feature. */}
          <div className="grid grid-cols-1 gap-2 @3xl:grid-cols-3">
            {STATES.map((s) => {
              const n = known ? count(s.state) : null
              const on = only === s.state
              const alarm = s.state === 'hidden' && !!n
              return (
                <button
                  key={s.state}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setOnly(on ? null : s.state)}
                  title={on ? 'Show every column again' : `Show only: ${s.name}`}
                  className={`flex flex-col items-start gap-1 rounded-md border px-3 py-2.5 text-left transition-colors ${
                    on ? 'border-(--color-navy-700) bg-(--color-navy-50) ring-1 ring-(--color-navy-700)'
                      : alarm ? 'border-(--color-overdue)/40 bg-(--color-overdue)/5 hover:border-(--color-overdue)/70'
                        : 'border-(--color-line) bg-(--color-surface) hover:border-(--color-navy-300) hover:bg-(--color-navy-50)'}`}
                >
                  <span className="flex w-full items-center gap-2">
                    <span className="font-semibold text-(--color-ink)">{s.name}</span>
                    <span className={`ml-auto text-(length:--text-lg) font-semibold tabular-nums ${alarm ? 'text-(--color-overdue)' : 'text-(--color-ink)'}`}>{n ?? '—'}</span>
                  </span>
                  <span className="text-(length:--text-small) leading-snug text-(--color-ink-3)">{s.what}</span>
                </button>
              )
            })}
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <FilterSlot label="Report" wide>
              <SearchSelect
                label="Report"
                value={reportSlug}
                onChange={(v) => { setOnly(null); onPickReport(v) }}
                disabled={!reports || reports.length === 0}
                placeholder={reports === null ? 'Loading…' : 'Choose a report'}
                options={(reports ?? []).map((r) => ({ value: r.slug, label: r.displayName }))}
              />
            </FilterSlot>
            <div className="flex min-w-[240px] flex-1 flex-col gap-1 @6xl:max-w-[360px]">
              <span className="eyebrow">Find a column</span>
              <SearchInput label="Search columns" placeholder="Column or permission…" value={search} onChange={setSearch} />
            </div>
            {roleScoped && <span className="pb-1.5"><Badge tone="neutral">role-scoped</Badge></span>}
            {!canEdit && (
              <span className="ml-auto pb-1.5 text-(length:--text-small) text-(--color-ink-3)">Changing who sees a column is a System Admin action.</span>
            )}
          </div>
        </div>

        {known && columns.length > 0 && (
          <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-b border-(--color-line) bg-(--color-surface-2) px-5 py-1.5 text-(length:--text-small) text-(--color-ink-3)">
            <span>
              Showing {visible.length} of {columns.length} {columns.length === 1 ? 'column' : 'columns'} on {report?.displayName ?? 'this report'}
              {only ? ` · ${stateName(only)} only` : ''}.
            </span>
            {(only || search) && (
              <button type="button" onClick={() => { setOnly(null); setSearch('') }} className="font-medium text-(--color-navy-700) hover:underline">
                Show every column
              </button>
            )}
            {hidden > 0 && !only && (
              <button type="button" onClick={() => setOnly('hidden')} className="ml-auto font-medium text-(--color-overdue) hover:underline">
                {hidden} {isV2 ? `seen by the system roles only` : 'hidden from everyone'}: look at {hidden === 1 ? 'it' : 'them'}
              </button>
            )}
          </div>
        )}

        {data === null ? (
          <Loading label="Reading the column mapping…" />
        ) : loadFailed ? (
          // Before the empty check on purpose: a read that failed says so, with
          // its reference, and never claims the report has no columns.
          <FailureState failure={loadFailed} onRetry={load} />
        ) : columns.length === 0 ? (
          // A role-scoped report has no column mask AT ALL — entitlement is the
          // platform role and there is no fact table to mask. Saying so is the
          // difference between "correctly empty" and "apparently broken".
          roleScoped ? (
            <EmptyState
              title={`${report?.displayName ?? 'This report'} has no column mask.`}
              detail="It is role-scoped: who may open it is decided by a platform role, and it reads no fact table with fields to gate. There is nothing to configure here."
            />
          ) : (
            <EmptyState
              title="No columns are registered for this report."
              detail={`Nothing would be visible to ${isV2 ? 'anyone without a system role' : 'anyone'}, because a column with no mapping is hidden from ${isV2 ? 'everyone else' : 'everyone'}. Columns are registered by the report's own deployment script.`}
            />
          )
        ) : visible.length === 0 ? (
          <EmptyState
            title={search ? `No column matches “${search}”${only ? ` among ${stateName(only)}` : ''}.` : `No column on this report is under “${stateName(only ?? 'gated')}”.`}
            detail="Clear the search or the card to see every column."
          />
        ) : (
          <DataGrid
            fill
            columns={grid}
            rows={visible}
            // No row click: a required row would light up and then do nothing. The
            // row being changed stays tinted while its panel is open.
            isRowActive={(c) => c.reportColumnId === editing?.reportColumnId}
          />
        )}

        <InfoStrip>
          <strong className="font-semibold text-(--color-ink-2)">D365 grants the module; this decides what the module unlocks.</strong>{' '}
          A user sees a column when they hold any one of the permissions listed against it. Their scope decides which
          rows they get; this decides which fields within those rows. Every change is recorded with its reason.
        </InfoStrip>
      </Card>

      {editing && (
        <ReGatePanel
          api={api}
          column={editing}
          catalogue={catalogue}
          isV2={isV2}
          onClose={() => setEditing(null)}
          onDone={(m) => { setEditing(null); setDone(m); setError(null); load() }}
          onError={(m) => { setEditing(null); setError(m) }}
        />
      )}
    </div>
  )
}

type GateState = 'always' | 'gated' | 'hidden'

function stateOf(c: ColumnGovernanceRow): GateState {
  return c.isRequired ? 'always' : c.permissions ? 'gated' : 'hidden'
}

function codesOf(c: ColumnGovernanceRow): string[] {
  return c.permissions ? c.permissions.split(/,\s*/) : []
}

/** A permission as people know it; the code itself where the catalogue has no name. */
function nameOf(catalogue: PermissionCatalogRow[], code: string): string {
  return catalogue.find((x) => x.permissionCode === code)?.displayName || code
}

/** The largest single permission's holder count: a floor on who can see the column. */
function holders(catalogue: PermissionCatalogRow[], codes: string[]): number {
  return codes.length === 0
    ? 0
    : Math.max(...codes.map((c) => catalogue.find((x) => x.permissionCode === c)?.peopleHolding ?? 0))
}

function PermissionChip({ name, code }: { name: string; code: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-(--color-line-strong) bg-(--color-surface-2) px-2 py-px text-(length:--text-small) text-(--color-ink)">
      {name}
      {name !== code && <span className="font-mono text-(length:--text-micro) text-(--color-ink-3)">{code}</span>}
    </span>
  )
}

/**
 * Re-gating one column.
 *
 * Tiles, not a text field: the permission vocabulary is fixed and small, and the
 * database rejects an unknown code rather than silently dropping it — so offering
 * free text would only ever produce an error the user could have been spared.
 * Each option carries its holder count, because that is the consequence being
 * chosen; the two ERP families (modules, cost figures) are grouped apart.
 */
function ReGatePanel({
  api,
  column,
  catalogue,
  isV2,
  onClose,
  onDone,
  onError,
}: {
  api: Api
  column: ColumnGovernanceRow
  catalogue: PermissionCatalogRow[]
  isV2: boolean
  onClose: () => void
  onDone: (message: string) => void
  onError: (message: string) => void
}) {
  const initial = codesOf(column)
  const [picked, setPicked] = useState<string[]>(initial)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  const toggle = (code: string) =>
    setPicked((p) => (p.includes(code) ? p.filter((x) => x !== code) : [...p, code]))

  const before = holders(catalogue, initial)
  const after = holders(catalogue, picked)
  const changed = picked.slice().sort().join(',') !== initial.slice().sort().join(',')
  const canSubmit = changed && !!reason.trim() && !busy

  const families: { title: string; rows: PermissionCatalogRow[] }[] = [
    { title: 'Modules', rows: catalogue.filter((p) => p.kind !== 'cost') },
    { title: 'Cost figures', rows: catalogue.filter((p) => p.kind === 'cost') },
  ].filter((f) => f.rows.length > 0)

  const submit = async () => {
    if (!canSubmit) return
    setBusy(true)
    try {
      const r = await api.setColumnPermissions({
        reportColumnId: column.reportColumnId,
        permissions: picked,
        reason: reason.trim(),
      })
      onDone(
        `${r.columnName}: ${r.before} → ${r.after}. ` +
          `${r.peopleBefore.toLocaleString('en-GB')} → ${r.peopleAfter.toLocaleString('en-GB')} ${r.peopleAfter === 1 ? 'user' : 'users'} can see it.`,
      )
    } catch (e) {
      onError(e instanceof ApiError ? e.message : 'The change was refused. Nothing was altered.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <SlideOver
      title={`Who can see ${column.displayName}?`}
      description={
        <>
          <span className="font-mono">{column.columnName}</span> — a user sees this column when
          they hold any one of the permissions ticked below.
        </>
      }
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            variant={picked.length === 0 ? 'danger' : 'primary'}
            onClick={() => void submit()}
            disabled={!canSubmit}
          >
            {busy ? 'Saving…' : picked.length === 0 ? (isV2 ? 'Show to system roles only' : 'Hide from everyone') : 'Save'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {families.map((f) => (
          <fieldset key={f.title} className="flex flex-col gap-1.5">
            <legend className="eyebrow mb-1.5">{f.title}</legend>
            {f.rows.map((p) => {
              const on = picked.includes(p.permissionCode)
              return (
                <CheckTile
                  key={p.permissionCode}
                  on={on}
                  onChange={() => toggle(p.permissionCode)}
                  hint={[p.permissionCode, p.notes].filter(Boolean).join(' · ')}
                  changed={on !== initial.includes(p.permissionCode)}
                >
                  <span className="flex items-center gap-2">
                    <span className="min-w-0 flex-1">{p.displayName}</span>
                    {/* A permission nobody holds hides the column as surely as
                        leaving it unmapped. Said here, where it is chosen. */}
                    {p.peopleHolding === 0 ? (
                      <Badge tone="bad">nobody holds it</Badge>
                    ) : (
                      <span className="shrink-0 text-(--color-ink-3) tabular-nums">{p.peopleHolding.toLocaleString('en-GB')} {p.peopleHolding === 1 ? 'user' : 'users'}</span>
                    )}
                  </span>
                </CheckTile>
              )
            })}
          </fieldset>
        ))}

        {/* THE EFFECT, IN PEOPLE, BEFORE THE PRESS.
            The audit ledger classifies this change as WIDEN or NARROW on
            exactly this comparison, so the screen shows the same arithmetic
            rather than a different summary of it. */}
        {changed && (
          after === 0 ? (
            <Verdict ok={false} title={isV2 ? 'Only the system roles will see this column.' : 'Nobody will see this column.'}>
              It will disappear from every page of the report for {isV2 ? 'everyone else' : 'everyone, including you'}.
            </Verdict>
          ) : (
            <Verdict ok={null} tone={after === before ? undefined : 'warn'}
              title={after > before ? 'More users will see it' : after < before ? 'Fewer users will see it' : 'About as many users will see it'}>
              Roughly {before.toLocaleString('en-GB')} {before === 1 ? 'user' : 'users'} can see it now;{' '}
              {after.toLocaleString('en-GB')} will after this change.
            </Verdict>
          )
        )}

        {/* The hint sits outside the label, tied by aria-describedby: inside it,
            the whole sentence became the box's name. */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="regate-reason" className="eyebrow">Reason Of The Change (required)</label>
          <textarea
            id="regate-reason"
            aria-describedby="regate-reason-hint"
            rows={3}
            className="rounded-md border border-(--color-line-strong) bg-(--color-surface) px-2.5 py-2 text-(length:--text-small) text-(--color-ink)"
            placeholder="e.g. Finance asked for credit data to be restricted to Finance holders"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <span id="regate-reason-hint" className="text-(length:--text-small) text-(--color-ink-3)">
            A column vanishing for a whole group has no user&apos;s name attached to explain it; this is the only
            answer to “why can nobody see Credit Limit any more?”.
          </span>
        </div>
      </div>
    </SlideOver>
  )
}
